import React from 'react';
import { ClipboardCheck, FileText, GitCompare, History, LayoutDashboard, ListChecks, Send, TrendingUp, User, Users } from 'lucide-react';
import { DevelopmentDemoProvider, useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { developmentDomains } from '../../data/developmentDomains';
import type { DevDomainId, DevSectionId } from '../../types/developmentDemo';
import { DemoShell, type ShellSection } from '../../components/demoShell/DemoShell';
import { WorkspaceChooser } from '../../components/demoShell/WorkspaceChooser';
import { DevParticipantOverview } from '../../components/developmentDemo/DevParticipantOverview';
import { DevTaskView } from '../../components/developmentDemo/DevTaskView';
import { DevParticipantPlan } from '../../components/developmentDemo/DevParticipantPlan';
import { DevProgressSection } from '../../components/developmentDemo/DevProgressSection';
import { DevHistory } from '../../components/developmentDemo/DevHistory';
import { DevReviewerOverview } from '../../components/developmentDemo/DevReviewerOverview';
import { ParticipantRecords } from '../../components/developmentDemo/ParticipantRecords';
import { EvidenceReview } from '../../components/developmentDemo/EvidenceReview';
import { ReviewerPlans } from '../../components/developmentDemo/ReviewerPlans';
import { CandidateComparison } from '../../components/developmentDemo/CandidateComparison';
import { PartnerReferrals } from '../../components/developmentDemo/PartnerReferrals';
import { MyReferrals } from '../../components/developmentDemo/MyReferrals';
const participantViews: Partial<Record<DevSectionId, React.ComponentType>> = {
  overview: DevParticipantOverview,
  task: DevTaskView,
  plan: DevParticipantPlan,
  progress: DevProgressSection,
  'my-referrals': MyReferrals,
  history: DevHistory
};
const reviewerViews: Partial<Record<DevSectionId, React.ComponentType>> = {
  overview: DevReviewerOverview,
  records: ParticipantRecords,
  review: EvidenceReview,
  plans: ReviewerPlans,
  progress: DevProgressSection,
  comparison: CandidateComparison,
  referrals: PartnerReferrals,
  history: DevHistory
};
export function DevelopmentDemoPage({
  domain
}: {
  domain: DevDomainId;
}) {
  return <DevelopmentDemoProvider config={developmentDomains[domain]} data-id="element-2847" data-mp="1jr6exaw0qmic">
      <DevelopmentDemo data-id="element-2848" data-mp="1esbmric5vllk" />
    </DevelopmentDemoProvider>;
}
function DevelopmentDemo() {
  const {
    state,
    config,
    dispatch,
    persistenceOk
  } = useDevelopmentDemo();
  const workspace = state.ui.workspace;
  if (!workspace) {
    return <WorkspaceChooser domainId={config.id} eyebrow={config.productLabel.toUpperCase()} title={config.title} description={config.description} persistenceOk={persistenceOk} cards={[{
      id: 'participant',
      ...config.workspaces.participant,
      icon: User,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'participant'
      })
    }, {
      id: 'reviewer',
      ...config.workspaces.reviewer,
      icon: Users,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'reviewer'
      })
    }]} footnote={<p className="rounded-lg border border-lavender/25 bg-lavender-pale px-4 py-3 text-sm leading-relaxed text-ink-dark" data-id="element-2850" data-mp="32qdsouorcj2a">
            Sample organization: {config.organization}. {config.note} Follows the Human Capability Framework cycle: Assess → Review → Develop →
            Reassess.
          </p>} data-id="element-2849" data-mp="27h0rdrcfe922" />;
  }
  const pendingReviews = state.submissions.filter(submission => !state.reviews.some(review => review.submissionId === submission.id)).length;
  const sections: ShellSection[] = workspace === 'participant' ? [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'task',
    label: 'My Task',
    icon: FileText
  }, {
    id: 'plan',
    label: 'Development Plan',
    icon: ListChecks
  }, {
    id: 'progress',
    label: 'My Progress',
    icon: TrendingUp
  }, ...(config.features.referrals ? [{
    id: 'my-referrals',
    label: 'My Referrals',
    icon: Send
  }] : []), {
    id: 'history',
    label: 'History & Controls',
    icon: History
  }] : [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'records',
    label: 'Participant Records',
    icon: Users
  }, {
    id: 'review',
    label: 'Evidence Review',
    icon: ClipboardCheck,
    count: pendingReviews
  }, ...(config.features.comparison ? [{
    id: 'comparison',
    label: 'Candidate Comparison',
    icon: GitCompare
  }] : []), {
    id: 'plans',
    label: 'Development Plans',
    icon: ListChecks
  }, {
    id: 'progress',
    label: 'Progress',
    icon: TrendingUp
  }, ...(config.features.referrals ? [{
    id: 'referrals',
    label: 'Partner Referrals',
    icon: Send
  }] : []), {
    id: 'history',
    label: 'History & Controls',
    icon: History
  }];
  const views = workspace === 'participant' ? participantViews : reviewerViews;
  const stored = state.ui.lastSection[workspace];
  const current = (sections.some(section => section.id === stored) ? stored : 'overview') as DevSectionId;
  const View = views[current] ?? DevHistory;
  const toolbar = workspace === 'participant' ? <div className="mb-8 flex flex-col gap-2 rounded-xl border border-line bg-white/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-2851" data-mp="20z5p74r46myh">
        <label htmlFor="acting-record" className="text-sm text-ink-muted" data-id="element-2852" data-mp="25yinclnfrggd">
          <span className="font-semibold text-ink-dark" data-id="element-2853" data-mp="2tnbb3utwmhzw">Demonstration access</span> · choose a fictional record. Not secure sign-in.
        </label>
        <select id="acting-record" value={state.ui.actingParticipantId} onChange={event => dispatch({
      type: 'setActingParticipant',
      id: event.target.value
    })} className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink-dark focus:border-lavender focus:outline-none focus:ring-2 focus:ring-lavender/20 sm:w-64" data-id="element-2854" data-mp="2xr98o13kzkqu">
          {config.participants.map(participant => <option key={participant.id} value={participant.id} data-id="element-2855" data-mp="3r9ny2c652v78">
              {participant.name}
            </option>)}
        </select>
      </div> : undefined;
  return <DemoShell domainId={config.id} productLabel={config.productLabel} workspaceLabel={config.workspaces[workspace].title} sections={sections} current={current} onSelect={id => dispatch({
    type: 'setSection',
    section: id as DevSectionId
  })} onSwitchWorkspace={() => dispatch({
    type: 'enterWorkspace',
    workspace: null
  })} persistenceOk={persistenceOk} toolbar={toolbar} data-id="element-2856" data-mp="3hayhqntw5it3">
      <View data-id="element-2857" data-mp="2ylicxob6b46v" />
    </DemoShell>;
}